/**
 * Document head helpers for title, description, Open Graph, Twitter, canonical.
 * Works without extra dependencies (CRA SPA).
 *
 * For marketing pages, pass `path` as the bare path ("/", "/pricing") and
 * `hreflang` true so alternate links use /es and /en.
 */

import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { absoluteLocalizedUrl, localizePath } from '../i18n/localePaths';

const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://clarcv.com').replace(/\/$/, '');
const DEFAULT_IMAGE = `${SITE_URL}/landing/og-share.png`;

const upsertMeta = (attr, key, content) => {
  if (content == null || content === '') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const attrEscape = (value) =>
  String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const upsertLink = (rel, href, attrs = {}) => {
  const attrEntries = Object.entries(attrs);
  let el = null;
  if (attrEntries.length) {
    const selector = attrEntries
      .map(([key, value]) => `[${key}="${attrEscape(value)}"]`)
      .join('');
    el = document.head.querySelector(`link[rel="${rel}"]${selector}`);
  } else {
    el = document.head.querySelector(`link[rel="${rel}"]:not([hreflang])`);
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    attrEntries.forEach(([key, value]) => el.setAttribute(key, value));
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const upsertJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const removeJsonLd = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

const removeHreflangLinks = () => {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
};

/**
 * Sets per-page SEO tags while the component is mounted.
 */
const Seo = ({
  title,
  description,
  path = '/',
  type = 'website',
  image,
  keywords,
  article,
  jsonLd,
  lang = 'es',
  hreflang = false,
  noindex = false,
}) => {
  useEffect(() => {
    const fullTitle = title.includes('ClarCV') ? title : `${title} · ClarCV`;
    const barePath = path.startsWith('/') ? path : `/${path}`;
    const canonicalPath = hreflang ? localizePath(barePath, lang) : barePath;
    const url = `${SITE_URL}${canonicalPath}`;
    const img = image || DEFAULT_IMAGE;
    const locale = lang === 'en' ? 'en_US' : 'es_ES';
    const altLocale = locale === 'en_US' ? 'es_ES' : 'en_US';

    document.title = fullTitle;
    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertMeta(
      'name',
      'robots',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large'
    );
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', img);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('property', 'og:site_name', 'ClarCV');
    upsertMeta('property', 'og:locale', locale);
    upsertMeta('property', 'og:locale:alternate', altLocale);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', img);
    upsertLink('canonical', url);

    removeHreflangLinks();
    if (hreflang && !noindex) {
      upsertLink('alternate', absoluteLocalizedUrl(SITE_URL, barePath, 'es'), {
        hreflang: 'es',
      });
      upsertLink('alternate', absoluteLocalizedUrl(SITE_URL, barePath, 'en'), {
        hreflang: 'en',
      });
      upsertLink('alternate', absoluteLocalizedUrl(SITE_URL, barePath, 'es'), {
        hreflang: 'x-default',
      });
    }

    if (type === 'article' && article) {
      upsertMeta('property', 'article:published_time', article.publishedTime);
      upsertMeta('property', 'article:modified_time', article.modifiedTime || article.publishedTime);
      if (article.section) upsertMeta('property', 'article:section', article.section);
      if (article.tags?.[0]) upsertMeta('property', 'article:tag', article.tags.join(', '));
    }

    if (jsonLd) {
      upsertJsonLd('clarcv-jsonld-page', Array.isArray(jsonLd) ? jsonLd : jsonLd);
    }

    return () => {
      removeJsonLd('clarcv-jsonld-page');
      removeHreflangLinks();
    };
  }, [title, description, path, type, image, keywords, article, jsonLd, lang, hreflang, noindex]);

  return null;
};

Seo.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  path: PropTypes.string,
  type: PropTypes.string,
  image: PropTypes.string,
  keywords: PropTypes.string,
  article: PropTypes.shape({
    publishedTime: PropTypes.string,
    modifiedTime: PropTypes.string,
    section: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }),
  jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  lang: PropTypes.string,
  hreflang: PropTypes.bool,
  noindex: PropTypes.bool,
};

export { SITE_URL };
export default Seo;
